import { type RoleNew } from "@vega-api/generated/types/roleNew";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { type BaseMultiSelectOption } from "@vega-core/components/Form/Controls/Dropdown/ThemedMultiselectDropdown/ThemedMultiselectDropdown";
import { SearchableDropdown } from "@vega-core/components/Form/Controls/SearchableDropdown/SearchableDropdown";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import {
  FUND_VEHICLE_TAB,
  FUND_VEHICLE_TAB_SELECTED_STYLES,
  type FundVehicleTab,
  fundVehicleTagConfig,
  getRoleOptionsForFundVehicleTab,
} from "./collaboratorsV2Constants";

type RoleAssignmentAccordionProps = {
  selectedRole: BaseMultiSelectOption;
  fundVehicleTab: FundVehicleTab;
  onRoleChange: (role: BaseMultiSelectOption) => void;
  onFundVehicleTabChange: (tab: FundVehicleTab) => void;
  roles: RoleNew[];
  disabledRoleIds?: string[];
};

export const RoleAssignmentAccordion = ({
  selectedRole,
  fundVehicleTab,
  onRoleChange,
  onFundVehicleTabChange,
  roles,
  disabledRoleIds = [],
}: RoleAssignmentAccordionProps) => {
  const disabledSet = new Set(disabledRoleIds);
  const rolesOptions = getRoleOptionsForFundVehicleTab(
    roles,
    fundVehicleTab,
  ).map((option) => ({
    ...option,
    disabled: disabledSet.has(option.value),
  }));

  const handleRoleDropdownChange = (
    newSelectedRole: { label: string; value: string } | undefined,
  ) => {
    const matchedRole = rolesOptions.find(
      (role) => role.value === newSelectedRole?.value,
    );
    onRoleChange(
      matchedRole
        ? { label: matchedRole.label, value: matchedRole.value }
        : { label: "", value: "" },
    );
  };

  const handleFundVehicleTabChange = (tab: FundVehicleTab) => {
    onFundVehicleTabChange(tab);
    const newOptions = getRoleOptionsForFundVehicleTab(roles, tab);

    if (!newOptions.some((option) => option.value === selectedRole.value)) {
      onRoleChange({ label: "", value: "" });
    }
  };

  return (
    <div className="flex flex-col gap-6 p-4">
      <SearchableDropdown
        label={m.common_role()}
        name="collaboratorsRoles"
        onChange={handleRoleDropdownChange}
        options={rolesOptions}
        placeholder={m.settings_collaborators_inviteCollaboratorSelectRole()}
        value={selectedRole.value ? selectedRole : undefined}
      />
      <div className="flex flex-col items-start gap-4">
        <span className="body-small-medium">
          {m.settings_collaborators_inviteCollaboratorFundVehicle()}
        </span>
        <div className="flex w-full gap-2">
          {(Object.values(FUND_VEHICLE_TAB) as FundVehicleTab[]).map((tab) => (
            <ThemedButton
              className={cnTwMerge(
                "flex-1",
                fundVehicleTab === tab && FUND_VEHICLE_TAB_SELECTED_STYLES[tab],
              )}
              dataTestId={`fund-vehicle-tab-${tab}`}
              key={tab}
              onClick={() => handleFundVehicleTabChange(tab)}
              type="button"
              variant="secondary"
            >
              {fundVehicleTagConfig[tab].label()}
            </ThemedButton>
          ))}
        </div>
      </div>
    </div>
  );
};
